/*
 * Hound-Tees size finder.
 *
 * Markup: snippets/ht-size-finder.liquid (the finder), snippets/ht-size-finder-trigger.liquid
 * (product page button and saved hounds), sections/ht-size-finder.liquid (page section) and the
 * size finder dialog in sections/main-product.liquid. The charts come from Theme settings > Size
 * finder, parsed by snippets/ht-size-finder-data.liquid.
 *
 * Sizing follows the chart's own rule: fit on the chest. When the chest sits on the line between
 * two sizes (or in a gap between two ranges), their length decides, and without a length the
 * theme setting does (the bigger size by default). "Other breed or mix" scores every fit on chest
 * first, then length.
 */
(function () {
  'use strict';

  const EPS = 0.001;
  const CM_PER_IN = 2.54;
  const HOUNDS_KEY = 'htsf:hounds';
  const UNIT_KEY = 'htsf:unit';
  const MAX_HOUNDS = 6;

  // Same codes as snippets/ht-size-finder-data.liquid, so "X Large" on a product matches XL.
  const SIZE_CODES = {
    xxs: '2XS', '2xs': '2XS', xxsmall: '2XS', '2xsmall': '2XS', extraextrasmall: '2XS',
    xs: 'XS', xsmall: 'XS', extrasmall: 'XS',
    s: 'S', sm: 'S', small: 'S',
    m: 'M', md: 'M', med: 'M', medium: 'M',
    l: 'L', lg: 'L', large: 'L',
    xl: 'XL', xlarge: 'XL', extralarge: 'XL',
    xxl: '2XL', '2xl': '2XL', xxlarge: '2XL', '2xlarge': '2XL', extraextralarge: '2XL',
    xxxl: '3XL', '3xl': '3XL', xxxlarge: '3XL', '3xlarge': '3XL'
  };

  function sizeCode(label) {
    const text = String(label == null ? '' : label);
    return SIZE_CODES[text.toLowerCase().replace(/[\s\-._]/g, '')] || text.trim().toUpperCase();
  }

  // '' -> null, '74' / '74 cm' / '29,5' / '28 1/2' / '28½' -> number, anything else -> NaN.
  function parseMeasure(value) {
    let text = String(value == null ? '' : value).trim();
    if (!text) return null;
    text = text
      .replace(',', '.')
      .replace(/\s*½/, '.5')
      .replace(/\s*¼/, '.25')
      .replace(/\s*¾/, '.75');
    const fraction = text.match(/^(\d+)\s+(\d+)\/(\d+)/);
    const number = fraction
      ? Number(fraction[1]) + Number(fraction[2]) / Number(fraction[3])
      : parseFloat(text);
    return Number.isFinite(number) && number > 0 ? number : NaN;
  }

  function convert(value, from, to) {
    if (from === to || typeof value !== 'number' || !Number.isFinite(value)) return value;
    return to === 'in' ? value / CM_PER_IN : value * CM_PER_IN;
  }

  function format(number) {
    return String(Math.round(number * 10) / 10);
  }

  function hasNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function rangeOf(size, dimension, unit) {
    const range = size && size[dimension] && size[dimension][unit];
    return Array.isArray(range) && range.length === 2 && range[0] > 0 ? range : null;
  }

  function distance(value, range) {
    if (!range) return 0;
    if (value < range[0] - EPS) return range[0] - value;
    if (value > range[1] + EPS) return value - range[1];
    return 0;
  }

  function sortedSizes(breed) {
    return (breed.sizes || [])
      .filter(size => rangeOf(size, 'chest', 'cm'))
      .sort((a, b) => a.chest.cm[0] - b.chest.cm[0]);
  }

  // Where a measurement falls among the sizes: inside one, between two, or off either end.
  function locate(value, sizes, dimension, unit) {
    const hits = [];
    sizes.forEach((size, index) => {
      const range = rangeOf(size, dimension, unit);
      if (range && value >= range[0] - EPS && value <= range[1] + EPS) hits.push(index);
    });
    if (hits.length === 1) return { type: 'in', index: hits[0] };
    if (hits.length > 1) return { type: 'between', lower: hits[0], upper: hits[hits.length - 1] };

    const ranged = sizes
      .map((size, index) => ({ index, range: rangeOf(size, dimension, unit) }))
      .filter(item => item.range);
    if (!ranged.length) return { type: 'none' };
    const last = ranged[ranged.length - 1];
    if (value < ranged[0].range[0]) return { type: 'below', index: ranged[0].index };
    if (value > last.range[1]) return { type: 'above', index: last.index };

    let lower = ranged[0].index;
    let upper = last.index;
    ranged.forEach(item => {
      if (item.range[1] < value) lower = item.index;
    });
    for (const item of ranged) {
      if (item.range[0] > value) {
        upper = item.index;
        break;
      }
    }
    return { type: 'between', lower, upper };
  }

  function recommendBreed(breed, input, rule) {
    const unit = input.unit;
    const sizes = sortedSizes(breed);
    if (!hasNumber(input.chest) || !sizes.length) return null;
    const spot = locate(input.chest, sizes, 'chest', unit);
    if (spot.type === 'none') return null;

    const hasLength = hasNumber(input.length);
    const rec = {
      mode: 'breed',
      breed,
      unit,
      chest: input.chest,
      length: hasLength ? input.length : null,
      reason: spot.type,
      size: null,
      lower: null,
      upper: null,
      lengthNote: null
    };

    if (spot.type === 'between') {
      rec.lower = sizes[spot.lower];
      rec.upper = sizes[spot.upper];
      const lowerLength = hasLength && rangeOf(rec.lower, 'length', unit);
      const upperLength = hasLength && rangeOf(rec.upper, 'length', unit);
      if (lowerLength && upperLength) {
        const toLower = distance(input.length, lowerLength);
        const toUpper = distance(input.length, upperLength);
        if (Math.abs(toLower - toUpper) > EPS) {
          rec.size = toLower < toUpper ? rec.lower : rec.upper;
          rec.reason = 'between-length';
        }
      }
      if (!rec.size) {
        rec.size = rule === 'down' ? rec.lower : rec.upper;
        rec.reason = rule === 'down' ? 'between-down' : 'between-up';
      }
    } else {
      rec.size = sizes[spot.index];
    }

    const lengthRange = rangeOf(rec.size, 'length', unit);
    if (hasLength && lengthRange) {
      if (input.length < lengthRange[0] - EPS) rec.lengthNote = 'short';
      else if (input.length > lengthRange[1] + EPS) rec.lengthNote = 'long';
      else rec.lengthNote = 'ok';
    }
    rec.chestOff = distance(input.chest, rangeOf(rec.size, 'chest', unit));
    rec.lengthOff = hasLength ? distance(input.length, lengthRange) : 0;
    return rec;
  }

  // Chest counts double: the chart fits on chest, length only breaks ties between fits.
  function score(rec) {
    return rec.chestOff * 2 + rec.lengthOff;
  }

  function recommendAny(breeds, input, rule) {
    const recs = breeds.map(breed => recommendBreed(breed, input, rule)).filter(Boolean);
    if (!recs.length) return null;
    recs.sort((a, b) => score(a) - score(b));
    const best = Object.assign({}, recs[0], { mode: 'any' });
    best.ties = recs.slice(1).filter(rec => Math.abs(score(rec) - score(recs[0])) < EPS);
    return best;
  }

  // A fit the measurements suit when the chosen one doesn't (off the chart, or well off in length).
  function suggestFit(breeds, rec, input, rule) {
    const lengthWay = rec.lengthOff >= (rec.unit === 'in' ? 1.25 : 3);
    if (rec.chestOff < EPS && !lengthWay) return null;
    const others = breeds.filter(breed => breed.id !== rec.breed.id);
    const other = recommendAny(others, input, rule);
    if (!other || other.chestOff > EPS || other.lengthOff > EPS) return null;
    return score(other) < score(rec) ? other : null;
  }

  // "74" with inches selected, or "28" with cm: suggest the other unit when that fits the chart.
  function unitHint(breeds, breed, input, rule) {
    const pool = breed ? [breed] : breeds;
    const fits = pool.some(item => {
      const rec = recommendBreed(item, input, rule);
      return rec && rec.chestOff < EPS;
    });
    if (fits) return null;
    const other = Object.assign({}, input, { unit: input.unit === 'cm' ? 'in' : 'cm' });
    const rec = breed ? recommendBreed(breed, other, rule) : recommendAny(breeds, other, rule);
    return rec && rec.chestOff < EPS ? rec : null;
  }

  function normaliseWords(text) {
    return ' ' + String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  }

  // The breed named in a product option value, e.g. "Italian Greyhound" or "Iggy Long Sleeve".
  // The longest name wins, so "Italian Greyhound" is never read as "Greyhound".
  function matchBreed(breeds, text) {
    const haystack = normaliseWords(text);
    let best = null;
    let bestLength = 0;
    breeds.forEach(breed => {
      [breed.name, breed.id].forEach(word => {
        const needle = normaliseWords(word);
        if (needle.trim() && haystack.includes(needle) && needle.length > bestLength) {
          best = breed;
          bestLength = needle.length;
        }
      });
    });
    return best;
  }

  const core = {
    sizeCode,
    parseMeasure,
    convert,
    locate,
    recommendBreed,
    recommendAny,
    suggestFit,
    unitHint,
    matchBreed
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements) return;
  window.HtSizeFinderCore = core;

  const storage = {
    get(key) {
      try {
        return JSON.parse(window.localStorage.getItem(key));
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // Private browsing or storage full: the finder still works, it just won't remember.
      }
    }
  };

  function newId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  // Saved hounds, newest first. Each has an id, so editing one (its name included) updates it
  // rather than adding another.
  function loadHounds() {
    const stored = storage.get(HOUNDS_KEY);
    if (!Array.isArray(stored)) return [];
    let missingIds = false;
    const hounds = stored
      .filter(hound => hound && typeof hound === 'object' && hasNumber(hound.chest))
      .map(hound => {
        if (hound.id) return hound;
        missingIds = true;
        return Object.assign({ id: newId() }, hound);
      });
    if (missingIds) storage.set(HOUNDS_KEY, hounds);
    return hounds;
  }

  const filterCache = new Map();

  // A collection page's Search & Discovery filter values. Sizes are grouped there, so a size's
  // value is an id like gid://shopify/FilterSettingGroup/…, not "Medium": read them off the page.
  function collectionFilters(url) {
    if (!filterCache.has(url)) {
      filterCache.set(
        url,
        fetch(url)
          .then(response => (response.ok ? response.text() : ''))
          .then(html => {
            const page = new DOMParser().parseFromString(html, 'text/html');
            return Array.from(page.querySelectorAll('input[name^="filter.v."]')).map(input => {
              const label =
                (input.id && page.querySelector(`label[for="${CSS.escape(input.id)}"]`)) ||
                input.closest('label');
              return {
                name: input.name,
                value: input.value,
                label: label ? label.textContent.replace(/\(\d+\)/g, '').trim() : ''
              };
            });
          })
          .catch(() => [])
      );
    }
    return filterCache.get(url);
  }

  // What's on screen, to tell whether it matches the saved hound.
  function houndKey(input) {
    return [input.fit, input.chest, input.length, String(input.name || '').toLowerCase(), input.unit].join('|');
  }

  function readJson(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  // Shopify HTML-escapes translations ("We&#39;d go"). Fine in markup, but these strings arrive
  // as JSON and go out through textContent, so decode them once.
  function decodeStrings(strings) {
    const decoder = document.createElement('textarea');
    Object.keys(strings || {}).forEach(key => {
      decoder.innerHTML = strings[key];
      strings[key] = decoder.value;
    });
    return strings || {};
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    );
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function track(name, detail) {
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(name, detail);
    } catch (error) {
      // Analytics must never break the finder.
    }
  }

  // Klaviyo: saved hounds go to the shopper's profile as "Found hound size" events ("Removed
  // hound" when one is removed), through the Klaviyo script the app embed loads. Klaviyo holds
  // events from shoppers it doesn't know yet and attaches them once they're identified (anonymous
  // backfill). Backfilled events keep their old time and may not trigger flows, so a hound first
  // sent anonymously is sent again, marked resent, once Klaviyo knows the shopper. Each event
  // carries the hound's id and when it was saved, so whatever receives them can keep one record
  // per hound and ignore an older event that arrives late.
  const KLAVIYO_KEY = 'htsf:klaviyo';
  const KLAVIYO_DELAY = 6000;
  let klaviyoOwner = null;

  function klaviyo() {
    if (window.Shopify && window.Shopify.designMode) return null;
    const api = window.klaviyo;
    return api && typeof api.track === 'function' ? api : null;
  }

  function klaviyoIdentified() {
    const api = klaviyo();
    if (!api || typeof api.isIdentified !== 'function') return Promise.resolve(false);
    return Promise.resolve()
      .then(() => api.isIdentified())
      .then(Boolean)
      .catch(() => false);
  }

  // Sahara's variant picker: one [data-input-wrapper] fieldset per option, holding a <select>
  // wrapped in <dropdown-input> or a group of radios.
  function optionFields(selector) {
    return Array.from(selector.querySelectorAll('[data-input-wrapper]')).map(fieldset => {
      const select = fieldset.querySelector('select');
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const field = select || radios[0];
      const name = ((field && field.name) || '').replace(/^options\[(.*)\]$/, '$1');
      return { fieldset, select, radios, name };
    });
  }

  function fieldValue(field) {
    if (field.select) return field.select.value;
    const checked = field.radios.find(radio => radio.checked);
    return checked ? checked.value : '';
  }

  function fieldHas(field, value) {
    return field.select
      ? Array.from(field.select.options).some(option => option.value === value)
      : field.radios.some(radio => radio.value === value);
  }

  function setFieldValue(field, value) {
    if (field.select) {
      const option = Array.from(field.select.options).find(item => item.value === value);
      if (!option) return false;
      const dropdown = field.select.closest('dropdown-input');
      const button =
        dropdown &&
        Array.from(dropdown.querySelectorAll('button[data-value]')).find(
          item => item.dataset.value === value
        );
      if (button && !button.disabled) {
        button.click();
        return true;
      }
      Array.from(field.select.options).forEach(item =>
        item.toggleAttribute('selected', item === option)
      );
      field.select.value = value;
      field.select.dispatchEvent(new Event('change', { bubbles: true }));
      if (dropdown && typeof dropdown.update === 'function') dropdown.update();
      return true;
    }
    const radio = field.radios.find(item => item.value === value);
    if (!radio) return false;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  class HtSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.breeds = readJson(this.querySelector('[data-htsf-data]')) || [];
      this.strings = decodeStrings(readJson(this.querySelector('[data-htsf-strings]')));
      this.form = this.querySelector('[data-htsf-form]');
      if (!this.form || !this.breeds.length) return;

      this.resultEl = this.querySelector('[data-htsf-result]');
      this.errorEl = this.querySelector('[data-htsf-error]');
      this.noteEl = this.querySelector('[data-htsf-note]');
      this.savedEl = this.querySelector('[data-htsf-saved]');
      this.chestInput = this.querySelector('[data-htsf-input="chest"]');
      this.lengthInput = this.querySelector('[data-htsf-input="length"]');
      this.nameInput = this.querySelector('[data-htsf-input="name"]');
      this.rule = this.dataset.between === 'down' ? 'down' : 'up';
      this.remember = this.hasAttribute('data-remember');
      this.chartMode = this.dataset.chart || 'selected';
      this.productBreeds = (this.dataset.productBreeds || '').split(',').filter(Boolean);
      const savedUnit = storage.get(UNIT_KEY);
      this.unit = savedUnit === 'cm' || savedUnit === 'in' ? savedUnit : this.dataset.unit === 'in' ? 'in' : 'cm';
      this.syncUnit();

      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.update({ commit: true, reveal: true });
      });
      this.form.addEventListener('input', event => this.onInput(event));
      this.form.addEventListener('change', event => this.onChange(event));
      this.addEventListener('click', event => this.onClick(event));

      this.klaviyoOn = this.remember && this.hasAttribute('data-klaviyo');
      this.prefill();
      this.update();
      if (this.klaviyoOn) this.startKlaviyo();
    }

    // --- Reading and writing the form -----------------------------------------------------

    read() {
      const fit = this.form.querySelector('.htsf-breed__input:checked');
      return {
        fit: fit ? fit.value : '',
        chest: parseMeasure(this.chestInput.value),
        length: parseMeasure(this.lengthInput.value),
        name: this.nameInput ? this.nameInput.value.trim().slice(0, 24) : '',
        unit: this.unit
      };
    }

    breedById(id) {
      return this.breeds.find(breed => breed.id === id) || null;
    }

    checkFit(id) {
      const input = Array.from(this.form.querySelectorAll('.htsf-breed__input')).find(
        item => item.value === id
      );
      if (input) input.checked = true;
      return Boolean(input);
    }

    setUnit(unit, convertValues) {
      if ((unit !== 'cm' && unit !== 'in') || unit === this.unit) return;
      if (convertValues) {
        [this.chestInput, this.lengthInput].forEach(input => {
          const value = parseMeasure(input.value);
          if (hasNumber(value)) input.value = format(convert(value, this.unit, unit));
        });
      }
      this.unit = unit;
      storage.set(UNIT_KEY, unit);
      this.syncUnit();
    }

    syncUnit() {
      this.dataset.unit = this.unit;
      this.form.querySelectorAll('.htsf-unit__input').forEach(input => {
        input.checked = input.value === this.unit;
      });
      this.querySelectorAll('[data-htsf-unit-label]').forEach(label => {
        label.textContent = this.unit;
      });
    }

    // --- Events ------------------------------------------------------------------------------

    onInput(event) {
      if (!event.target.matches('[data-htsf-input]')) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update({ live: true }), 450);
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('.htsf-unit__input')) {
        this.setUnit(target.value, true);
        this.update({ commit: true });
      } else if (target.matches('.htsf-breed__input, [data-htsf-input]')) {
        this.update({ commit: true });
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-htsf-action]');
      if (!button || !this.contains(button)) return;
      const action = button.dataset.htsfAction;
      if (action === 'select') this.selectRecommended();
      if (action === 'unit') {
        // Their number was right, the unit wasn't: keep the number, change the unit.
        this.setUnit(button.dataset.unit, false);
        this.update({ commit: true });
      }
      if (action === 'fit' && this.checkFit(button.dataset.fit)) this.update({ commit: true });
      if (action === 'forget') this.forget();
      if (action === 'add') this.addHound();
      if (action === 'load' || action === 'remove') {
        const hound = loadHounds().find(item => item.id === button.dataset.hound);
        if (hound && action === 'load') {
          this.load(hound);
          this.renderSaved();
          this.update();
        }
        if (hound && action === 'remove') this.remove(hound.id);
      }
      if (action === 'shop') this.shop(event, button);
    }

    onOpen() {
      if (!this.ready || !this.form) return;
      if (!this.chestInput.value) this.prefill();
      this.renderSaved();
      this.update();
      if (window.matchMedia('(pointer: fine)').matches) {
        const empty = this.read().fit ? this.chestInput : this.form.querySelector('.htsf-breed__input');
        if (empty && !this.chestInput.value) empty.focus();
      }
    }

    // --- Recommending --------------------------------------------------------------------------

    recommend(input) {
      if (input.fit === 'any') return recommendAny(this.breeds, input, this.rule);
      const breed = this.breedById(input.fit);
      return breed ? recommendBreed(breed, input, this.rule) : null;
    }

    recommendFor(hound) {
      return this.recommend({
        fit: hound.fit,
        chest: hound.chest,
        length: hasNumber(hound.length) ? hound.length : null,
        name: hound.name || '',
        unit: hound.unit === 'in' ? 'in' : 'cm'
      });
    }

    // Typing "7" on the way to "74": wait for the rest rather than flash "too small".
    isPartial(input) {
      const pool = input.fit === 'any' ? this.breeds : [this.breedById(input.fit)].filter(Boolean);
      const smallest = Math.min(
        ...pool.map(breed => {
          const sizes = sortedSizes(breed);
          const range = sizes.length ? rangeOf(sizes[0], 'chest', input.unit) : null;
          return range ? range[0] : Infinity;
        })
      );
      return Number.isFinite(smallest) && input.chest < smallest / 2;
    }

    update(options = {}) {
      clearTimeout(this.timer);
      const input = this.read();
      this.renderNote(input.fit);

      let error = '';
      if (!input.fit) error = 'error_fit';
      else if (input.chest === null) error = 'error_chest';
      else if (Number.isNaN(input.chest) || Number.isNaN(input.length)) error = 'error_number';
      if (error) {
        const loud = options.reveal || (options.commit && input.fit && error === 'error_number');
        this.setError(loud ? this.strings[error] : '');
        this.rec = null;
        this.render(null, input);
        this.showCharts(input.fit === 'any' ? '' : input.fit);
        return null;
      }
      if (options.live && this.isPartial(input)) return this.rec;

      const rec = this.recommend(input);
      this.setError('');
      if (rec) {
        const breed = input.fit === 'any' ? null : rec.breed;
        rec.unitHint = unitHint(this.breeds, breed, input, this.rule);
        rec.suggestion = !rec.unitHint && rec.mode === 'breed' ? suggestFit(this.breeds, rec, input, this.rule) : null;
      }
      this.rec = rec;
      this.input = input;
      if (rec && (options.commit || options.reveal)) this.save(input);
      this.render(rec, input);
      this.showCharts(rec ? rec.breed.id : input.fit);

      if (rec && (options.commit || options.reveal)) {
        track('ht_size_finder_result', {
          fit: input.fit,
          breed: rec.breed.id,
          size: rec.size.label,
          reason: rec.reason,
          unit: input.unit,
          has_length: input.length !== null,
          on_product: Boolean(this.dataset.sectionId)
        });
      }
      if (rec && options.reveal) {
        // In the dialog, to the top of the result; on a page, clear of the sticky header.
        const block = this.closest('dialog') ? 'start' : 'center';
        this.resultEl.scrollIntoView({ behavior: 'smooth', block });
      }
      return rec;
    }

    // --- Rendering -------------------------------------------------------------------------------

    setError(text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text || '';
      this.errorEl.hidden = !text;
    }

    renderNote(fit) {
      if (!this.noteEl) return;
      const breed = this.breedById(fit);
      let text = '';
      if (fit === 'any') text = this.strings.other_note;
      else if (breed && breed.also) text = fill(this.strings.also_fits, { breeds: breed.also });
      this.noteEl.textContent = text || '';
      this.noteEl.hidden = !text;
    }

    showCharts(breedId) {
      if (this.chartMode === 'all') return;
      this.querySelectorAll('[data-htsf-chart]').forEach(chart => {
        chart.classList.toggle('htsf-chart--inactive', chart.dataset.htsfChart !== breedId);
      });
    }

    highlight(rec) {
      this.querySelectorAll('.htsf-table tr.is-match').forEach(row => row.classList.remove('is-match'));
      if (this.matchLabel) this.matchLabel.remove();
      if (!rec) return;
      const chart = this.querySelector(`[data-htsf-chart="${CSS.escape(rec.breed.id)}"]`);
      const row = chart && chart.querySelector(`tr[data-size="${CSS.escape(rec.size.code)}"]`);
      if (!row) return;
      row.classList.add('is-match');
      this.matchLabel = this.matchLabel || element('span', 'visually-hidden', ` (${this.strings.chart_match})`);
      row.querySelector('th').append(this.matchLabel);
    }

    range(size, dimension, unit) {
      const range = rangeOf(size, dimension, unit);
      if (!range) return '';
      const span = range[0] === range[1] ? format(range[0]) : `${format(range[0])}–${format(range[1])}`;
      return `${span} ${unit}`;
    }

    title(rec, name) {
      const s = this.strings;
      const values = { name, size: rec.size.label, breed: rec.breed.name };
      if (rec.reason === 'below') return fill(s.title_small, values);
      if (rec.reason === 'above') return fill(s.title_big, values);
      if (rec.mode === 'any') return fill(name ? s.title_fit_named : s.title_fit, values);
      return fill(name ? s.title_named : s.title, values);
    }

    reasons(rec) {
      const s = this.strings;
      const unit = rec.unit;
      const chest = `${format(rec.chest)} ${unit}`;
      const values = {
        chest,
        breed: rec.breed.name,
        size: rec.size.label,
        range: this.range(rec.size, 'chest', unit),
        lower: rec.lower ? rec.lower.label : '',
        upper: rec.upper ? rec.upper.label : ''
      };
      const lines = [];
      if (rec.reason === 'below') {
        lines.push(fill(s.reason_small, Object.assign({}, values, { value: `${format(rangeOf(rec.size, 'chest', unit)[0])} ${unit}` })));
      } else if (rec.reason === 'above') {
        lines.push(fill(s.reason_big, Object.assign({}, values, { value: `${format(rangeOf(rec.size, 'chest', unit)[1])} ${unit}` })));
      } else if (rec.mode === 'any') {
        lines.push(fill(s.reason_any, values));
      } else if (rec.reason === 'in') {
        lines.push(fill(s.reason_in, values));
      } else if (rec.reason === 'between-length') {
        lines.push(fill(s.reason_between_length, values));
      } else {
        lines.push(fill(rec.reason === 'between-down' ? s.reason_between_down : s.reason_between_up, values));
        if (rec.length === null) lines.push(s.add_length);
      }

      if (rec.mode === 'any' && rec.ties && rec.ties.length) {
        const options = rec.ties.map(tie => `${tie.breed.name} ${tie.size.label}`).join(` ${s.or} `);
        lines.push(fill(s.also_fit, { options }));
        if (rec.length === null) lines.push(s.add_length);
      }

      if (rec.lengthNote && rec.reason !== 'between-length') {
        const key = { ok: 'length_ok', long: 'length_long', short: 'length_short' }[rec.lengthNote];
        lines.push(fill(s[key], { size: rec.size.label, range: this.range(rec.size, 'length', unit) }));
      }
      return lines;
    }

    render(rec, input) {
      const box = this.resultEl;
      box.textContent = '';
      this.highlight(rec);
      if (!rec) {
        box.hidden = true;
        return;
      }
      const s = this.strings;
      box.hidden = false;

      const card = element('div', 'htsf-result');
      card.style.setProperty('--htsf-size', rec.size.color || '#E6E6E6');
      if (rec.reason === 'below' || rec.reason === 'above') card.classList.add('htsf-result--off');
      const badge = element('div', 'htsf-result__badge', rec.size.code);
      badge.setAttribute('aria-hidden', 'true');
      const body = element('div', 'htsf-result__body');
      body.append(
        element('p', 'htsf-result__eyebrow', fill(s.fit_label, { breed: rec.breed.name })),
        element('h3', 'htsf-result__title', this.title(rec, input.name))
      );
      this.reasons(rec).forEach(line => body.append(element('p', 'htsf-result__line', line)));

      if (rec.unitHint) {
        const other = rec.unitHint.unit;
        const hint = element('div', 'htsf-result__hint');
        hint.append(
          element(
            'p',
            '',
            fill(s.unit_check, {
              unit: other === 'in' ? s.in_name : s.cm_name,
              value: `${format(rec.chest)} ${other}`,
              size: rec.unitHint.mode === 'any' ? `${rec.unitHint.breed.name} ${rec.unitHint.size.label}` : rec.unitHint.size.label
            })
          ),
          this.button(fill(s.unit_switch, { unit: other === 'in' ? s.in_name : s.cm_name }), 'unit', { unit: other })
        );
        body.append(hint);
      }

      if (rec.suggestion) {
        const hint = element('div', 'htsf-result__hint');
        hint.append(
          element('p', '', fill(s.suggest_fit, { breed: rec.suggestion.breed.name, size: rec.suggestion.size.label })),
          this.button(fill(s.use_fit, { breed: rec.suggestion.breed.name }), 'fit', { fit: rec.suggestion.breed.id })
        );
        body.append(hint);
      }

      // Well off the chart (more than a couple of cm), the end size won't fit: no Select, and the
      // shop link goes to the fit they do suit, if there is one.
      const offChart = (rec.reason === 'below' || rec.reason === 'above') && rec.chestOff > (rec.unit === 'in' ? 0.75 : 2);
      const shopFor = offChart ? rec.suggestion : rec;
      const actions = element('div', 'htsf-result__actions');
      if (this.dataset.sectionId && !offChart) this.renderProduct(rec, body, actions);
      else if (shopFor && shopFor.breed.url) actions.append(this.shopLink(shopFor.breed, shopFor.size));
      if (actions.childNodes.length) body.append(actions);

      const fitNote = this.querySelector('template[data-htsf-fit-note]');
      if (fitNote) {
        const note = element('div', 'htsf-result__note');
        note.append(fitNote.content.cloneNode(true));
        body.append(note);
      }

      if (this.remember && this.savedKey === houndKey(input)) {
        const saved = element('p', 'htsf-result__saved', `${s.saved} `);
        saved.append(this.button(s.forget, 'forget', {}, 'htsf-link'));
        body.append(saved);
      }

      card.append(badge, body);
      box.append(card);
    }

    button(text, action, data, className) {
      const button = element('button', className || 'htsf-btn htsf-btn--small', text);
      button.type = 'button';
      button.dataset.htsfAction = action;
      Object.keys(data || {}).forEach(key => {
        button.dataset[key] = data[key];
      });
      return button;
    }

    // "Shop Greyhound styles in Medium": the fit's collection, filtered to their size. The filter
    // value is looked up when the shopper points at the link (or on tap), and if that fails or
    // the collection has no size filter, the link still opens the collection.
    shopLink(breed, size) {
      const link = element('a', 'htsf-btn', fill(this.strings.shop_size, { breed: breed.name, size: size.label }));
      link.href = breed.url;
      link.dataset.htsfAction = 'shop';
      link.dataset.size = size.code;
      const prepare = () =>
        this.filteredUrl(breed.url, size.code).then(url => {
          link.href = url;
          link.dataset.ready = 'true';
        });
      link.addEventListener('pointerenter', prepare, { once: true });
      link.addEventListener('focus', prepare, { once: true });
      return link;
    }

    shop(event, link) {
      this.flushKlaviyo();
      if (link.dataset.ready || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      link.classList.add('is-loading');
      link.setAttribute('aria-busy', 'true');
      this.filteredUrl(link.getAttribute('href'), link.dataset.size).then(url => {
        window.location.href = url;
      });
    }

    filteredUrl(url, code) {
      const option = `filter.v.option.${(this.dataset.sizeOption || 'Size').toLowerCase()}`;
      const lookup = collectionFilters(url).then(filters => {
        const size = filters.find(filter => filter.name === option && sizeCode(filter.label) === code);
        if (!size) return url;
        const params = new URLSearchParams([[size.name, size.value]]);
        // If the store switches on Search & Discovery's Availability filter, use it too.
        const inStock = filters.find(filter => filter.name === 'filter.v.availability' && filter.value === '1');
        if (inStock) params.append(inStock.name, inStock.value);
        return `${url}${url.includes('?') ? '&' : '?'}${params}`;
      });
      const slow = new Promise(resolve => setTimeout(() => resolve(url), 3000));
      return Promise.race([lookup, slow]);
    }

    // --- Product pages ---------------------------------------------------------------------------

    selector() {
      const id = this.dataset.sectionId;
      return id ? document.querySelector(`product-selector[data-section-id="${CSS.escape(id)}"]`) : null;
    }

    // The variant to select for a recommendation: right breed and size, closest to what's selected.
    productMatch(rec) {
      const selector = this.selector();
      if (!selector) return { status: 'missing' };
      const fields = optionFields(selector);
      const variants = selector.variants || readJson(selector.querySelector('script[type="application/json"]')) || [];
      const sizeName = (this.dataset.sizeOption || 'Size').toLowerCase();
      let sizeIndex = fields.findIndex(field => field.name.toLowerCase() === sizeName);
      if (sizeIndex < 0) sizeIndex = fields.findIndex(field => /size/i.test(field.name));
      if (sizeIndex < 0) return { status: 'missing' };

      const breedIndex = fields.findIndex(field => /breed/i.test(field.name));
      const breedValues = breedIndex < 0 ? [] : [...new Set(variants.map(variant => variant.options[breedIndex]))];
      const current = fields.map(fieldValue);
      const candidates = variants.filter(variant => {
        if (sizeCode(variant.options[sizeIndex]) !== rec.size.code) return false;
        if (breedValues.length < 2) return true;
        const named = matchBreed(this.breeds, variant.options[breedIndex]);
        return !named || named.id === rec.breed.id;
      });
      if (!candidates.length) return { status: 'missing' };

      const present = variant => variant.options.every((value, index) => !fields[index] || fieldHas(fields[index], value));
      const ranked = candidates
        .map(variant => ({
          variant,
          rank:
            (present(variant) ? 100 : 0) +
            (variant.available ? 10 : 0) +
            variant.options.filter((value, index) => index !== sizeIndex && value === current[index]).length
        }))
        .sort((a, b) => b.rank - a.rank);
      const variant = ranked[0].variant;
      let status = 'available';
      if (!present(variant)) status = 'hidden';
      else if (!variant.available) status = 'soldout';
      return { status, variant, selector, sizeIndex };
    }

    renderProduct(rec, body, actions) {
      const s = this.strings;
      const values = { size: rec.size.label };
      if (!this.productBreeds.includes(rec.breed.id)) {
        const productBreed = this.breedById(this.productBreeds[0]);
        if (productBreed) body.append(element('p', 'htsf-result__stock', fill(s.other_fit, { breed: productBreed.name })));
        if (rec.breed.url) actions.append(this.shopLink(rec.breed, rec.size));
        return;
      }
      const match = this.productMatch(rec);
      const messages = {
        available: s.in_stock,
        soldout: s.sold_out_notify,
        hidden: s.sold_out,
        missing: s.not_made
      };
      const stock = element('p', `htsf-result__stock htsf-result__stock--${match.status}`, fill(messages[match.status], values));
      body.append(stock);
      if (match.status === 'available' || match.status === 'soldout') {
        actions.append(this.button(fill(s.select, values), 'select', {}, 'htsf-btn htsf-btn--primary'));
      } else if (rec.breed.url) {
        actions.append(this.shopLink(rec.breed, rec.size));
      }
    }

    selectRecommended() {
      if (!this.rec) return;
      if (this.input) this.save(this.input);
      this.flushKlaviyo();
      this.selectFor(this.rec);
    }

    // Selects the recommended variant in Sahara's picker, option by option (each change lets the
    // picker update which later values exist), then shows the shopper where it landed.
    selectFor(rec) {
      const match = this.productMatch(rec);
      if (!match.variant || match.status === 'hidden') return false;
      match.variant.options.forEach((value, index) => {
        const field = optionFields(match.selector)[index];
        if (field && fieldValue(field) !== value) setFieldValue(field, value);
      });
      track('ht_size_finder_select', {
        breed: rec.breed.id,
        size: rec.size.label,
        variant_id: match.variant.id
      });

      const dialog = this.closest('dialog');
      if (dialog && dialog.open) {
        dialog.htsfOpener = null;
        dialog.close();
      }
      const field = optionFields(match.selector)[match.sizeIndex];
      if (field) {
        field.fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.fieldset.classList.remove('htsf-flash');
        void field.fieldset.offsetWidth;
        field.fieldset.classList.add('htsf-flash');
        const focusable = field.fieldset.querySelector('summary, input:checked, select:not(.hidden)');
        if (focusable) focusable.focus({ preventScroll: true });
      }
      window.dispatchEvent(new CustomEvent('htsf:selected', { detail: { size: rec.size.label } }));
      return true;
    }

    // --- Klaviyo ---------------------------------------------------------------------------------

    // One finder per page looks after syncing, and sends anything waiting when the page is left.
    startKlaviyo() {
      if (klaviyoOwner) return;
      klaviyoOwner = this;
      const flush = () =>
        document.querySelectorAll('ht-size-finder').forEach(finder => {
          if (typeof finder.flushKlaviyo === 'function') finder.flushKlaviyo();
        });
      window.addEventListener('pagehide', flush);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
      });
      // A Klaviyo sign-up form identifies the shopper: re-send what Klaviyo only had anonymously.
      window.addEventListener('klaviyoForms', event => {
        if (event.detail && event.detail.type === 'submit') setTimeout(() => this.syncKlaviyo(), 2000);
      });
      this.syncKlaviyo();
    }

    klaviyoProperties(hound) {
      const rec = this.recommendFor(hound);
      if (!rec) return null;
      const unit = hound.unit === 'in' ? 'in' : 'cm';
      const cm = value => (hasNumber(value) ? Math.round(convert(value, unit, 'cm') * 10) / 10 : null);
      const properties = {
        hound_id: hound.id,
        hound_name: hound.name || '',
        fit: hound.fit,
        breed: rec.breed.id,
        breed_name: rec.breed.name,
        size: rec.size.label,
        size_code: rec.size.code,
        chest_cm: cm(hound.chest),
        length_cm: cm(hound.length),
        unit,
        saved_at: hound.saved ? new Date(hound.saved).toISOString() : null,
        page: this.dataset.sectionId ? 'product' : 'page',
        product_handle: this.dataset.productHandle || null,
        product_title: this.dataset.productTitle || null
      };
      Object.keys(properties).forEach(key => {
        if (properties[key] === null) delete properties[key];
      });
      return properties;
    }

    // Sends a hound unless Klaviyo already has it as it is now; `resend` sends it again regardless.
    sendHound(hound, resend) {
      const api = klaviyo();
      const properties = api && this.klaviyoProperties(hound);
      if (!properties) return;
      const sig = JSON.stringify([
        properties.hound_name, properties.fit, properties.breed,
        properties.size_code, properties.chest_cm, properties.length_cm
      ]);
      const sent = storage.get(KLAVIYO_KEY) || {};
      if (!resend && sent[hound.id] && sent[hound.id].sig === sig) return;
      klaviyoIdentified().then(identified => {
        if (resend) properties.resent = true;
        api.track('Found hound size', properties);
        const latest = storage.get(KLAVIYO_KEY) || {};
        latest[hound.id] = { sig, identified };
        storage.set(KLAVIYO_KEY, latest);
      });
    }

    // Edits come in field by field, so wait for a pause before sending.
    queueKlaviyo(id) {
      if (!this.klaviyoOn) return;
      if (this.klaviyoPending && this.klaviyoPending !== id) this.flushKlaviyo();
      this.klaviyoPending = id;
      clearTimeout(this.klaviyoTimer);
      this.klaviyoTimer = setTimeout(() => this.flushKlaviyo(), KLAVIYO_DELAY);
    }

    flushKlaviyo() {
      clearTimeout(this.klaviyoTimer);
      const id = this.klaviyoPending;
      this.klaviyoPending = null;
      const hound = id && loadHounds().find(item => item.id === id);
      if (hound) this.sendHound(hound, false);
    }

    // Sends hounds Klaviyo hasn't had (saved before this was on, or while its script was blocked),
    // and re-sends ones it only had anonymously once it knows the shopper.
    syncKlaviyo() {
      if (!this.klaviyoOn || !klaviyo()) return;
      klaviyoIdentified().then(identified => {
        const sent = storage.get(KLAVIYO_KEY) || {};
        loadHounds().forEach(hound => {
          const record = sent[hound.id];
          if (!record) this.sendHound(hound, false);
          else if (identified && !record.identified) this.sendHound(hound, true);
        });
      });
    }

    klaviyoRemoved(id) {
      if (this.klaviyoPending === id) {
        clearTimeout(this.klaviyoTimer);
        this.klaviyoPending = null;
      }
      const api = klaviyo();
      const sent = storage.get(KLAVIYO_KEY) || {};
      if (!this.klaviyoOn || !api || !sent[id]) return;
      api.track('Removed hound', { hound_id: id });
      delete sent[id];
      storage.set(KLAVIYO_KEY, sent);
    }

    // --- Saved hounds ----------------------------------------------------------------------------

    houndFit(hound) {
      if (hound.fit !== 'any') return hound.fit;
      const rec = this.recommendFor(hound);
      return rec ? rec.breed.id : '';
    }

    prefill() {
      const hounds = this.remember ? loadHounds() : [];
      const hound = this.productBreeds.length
        ? hounds.find(item => this.productBreeds.includes(this.houndFit(item)))
        : hounds[0];
      if (hound) this.load(hound);
      else if (this.productBreeds.length) this.checkFit(this.currentProductBreed());
      this.renderSaved();
    }

    // On a product made in several fits, the fit of the breed option that's selected.
    currentProductBreed() {
      const selector = this.selector();
      if (selector) {
        const fields = optionFields(selector);
        const breedField = fields.find(field => /breed/i.test(field.name));
        const breed = breedField && matchBreed(this.breeds, fieldValue(breedField));
        if (breed && this.productBreeds.includes(breed.id)) return breed.id;
      }
      return this.productBreeds[0];
    }

    load(hound) {
      const unit = hound.unit === 'in' ? 'in' : 'cm';
      if (!this.checkFit(hound.fit) && this.productBreeds.length) this.checkFit(this.currentProductBreed());
      this.chestInput.value = format(convert(hound.chest, unit, this.unit));
      this.lengthInput.value = hasNumber(hound.length) ? format(convert(hound.length, unit, this.unit)) : '';
      if (this.nameInput) this.nameInput.value = hound.name || '';
      this.currentId = hound.id;
      this.savedKey = houndKey(this.read());
    }

    // Saves the hound on screen. Editing it (its name included) updates it in place; a new hound
    // starts from "Add a hound", or on a page none of the saved hounds fit. Typing the name of a
    // saved hound means that hound.
    save(input) {
      if (!this.remember || !hasNumber(input.chest) || !input.fit) return;
      const hounds = loadHounds();
      const name = input.name.toLowerCase();
      const hound =
        hounds.find(item => item.id === this.currentId) ||
        (name && hounds.find(item => String(item.name || '').toLowerCase() === name)) ||
        { id: newId() };
      Object.assign(hound, {
        name: input.name,
        fit: input.fit,
        chest: input.chest,
        length: hasNumber(input.length) ? input.length : null,
        unit: input.unit,
        saved: Date.now()
      });
      storage.set(HOUNDS_KEY, [hound, ...hounds.filter(item => item.id !== hound.id)].slice(0, MAX_HOUNDS));
      this.currentId = hound.id;
      this.savedKey = houndKey(input);
      this.renderSaved();
      this.queueKlaviyo(hound.id);
      window.dispatchEvent(new CustomEvent('htsf:saved'));
    }

    // Empties the form for a new, unsaved hound.
    clear() {
      this.currentId = null;
      this.savedKey = null;
      this.chestInput.value = '';
      this.lengthInput.value = '';
      if (this.nameInput) this.nameInput.value = '';
    }

    addHound() {
      this.clear();
      if (this.productBreeds.length) this.checkFit(this.currentProductBreed());
      this.renderSaved();
      this.update();
      (this.read().fit ? this.chestInput : this.form.querySelector('.htsf-breed__input')).focus();
    }

    remove(id) {
      storage.set(HOUNDS_KEY, loadHounds().filter(hound => hound.id !== id));
      this.klaviyoRemoved(id);
      if (id === this.currentId) this.clear();
      this.renderSaved();
      this.update();
      window.dispatchEvent(new CustomEvent('htsf:saved'));
      const next = this.savedEl && this.savedEl.querySelector('button');
      (next || this.chestInput).focus();
    }

    forget() {
      if (this.currentId) {
        this.remove(this.currentId);
      } else {
        this.clear();
        this.update();
      }
      this.chestInput.focus();
    }

    // "Your hounds": tap one to switch to it, × to remove it, or add another.
    renderSaved() {
      if (!this.savedEl) return;
      const s = this.strings;
      const hounds = this.remember ? loadHounds() : [];
      this.savedEl.textContent = '';
      this.savedEl.hidden = !hounds.length;
      if (!hounds.length) return;
      this.savedEl.append(element('span', 'htsf__saved-label', s.saved_label));
      hounds.forEach(hound => {
        const rec = this.recommendFor(hound);
        const name = hound.name || s.your_hound;
        const current = hound.id === this.currentId;
        const chip = element('span', current ? 'htsf-hound is-current' : 'htsf-hound');
        const load = this.button(
          rec ? fill(s.chip, { name, size: rec.size.label }) : name,
          'load',
          { hound: hound.id },
          'htsf-hound__load'
        );
        load.setAttribute('aria-pressed', String(current));
        const remove = this.button('×', 'remove', { hound: hound.id }, 'htsf-hound__remove');
        remove.setAttribute('aria-label', fill(s.remove_hound, { name }));
        chip.append(load, remove);
        this.savedEl.append(chip);
      });
      this.savedEl.append(this.button(s.add_hound, 'add', {}, 'htsf-pill htsf-pill--add'));
    }
  }

  // "Find my size" on product pages, plus "Bea's size is Medium · Select" for saved hounds whose
  // size fits this product.
  class HtSizeFinderTrigger extends HTMLElement {
    connectedCallback() {
      this.savedEl = this.querySelector('[data-htsf-trigger-saved]');
      this.refresh = this.refresh.bind(this);
      window.addEventListener('htsf:saved', this.refresh);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-htsf-hound]');
        if (button) this.selectHound(button.dataset.htsfHound);
      });
      customElements.whenDefined('ht-size-finder').then(this.refresh);
    }

    disconnectedCallback() {
      window.removeEventListener('htsf:saved', this.refresh);
    }

    finder() {
      const dialog = document.getElementById(this.dataset.target);
      return dialog ? dialog.querySelector('ht-size-finder') : null;
    }

    refresh() {
      const finder = this.finder();
      if (!this.savedEl || !finder || !finder.form) return;
      const s = finder.strings;
      this.savedEl.textContent = '';
      const hounds = finder.remember ? loadHounds() : [];
      hounds.forEach(hound => {
        const rec = finder.recommendFor(hound);
        if (!rec || !finder.productBreeds.includes(rec.breed.id)) return;
        const line = element('p', 'htsf-saved-size');
        line.style.setProperty('--htsf-size', rec.size.color || '#E6E6E6');
        line.append(
          element('span', 'htsf-chip', rec.size.code),
          element(
            'span',
            'htsf-saved-size__text',
            hound.name
              ? fill(s.title_named, { name: hound.name, size: rec.size.label })
              : fill(s.chip, { name: s.your_hound, size: rec.size.label })
          )
        );
        const select = element('button', 'htsf-link', s.chip_select);
        select.type = 'button';
        select.dataset.htsfHound = hound.id;
        line.append(select);
        this.savedEl.append(line);
      });
      this.savedEl.hidden = !this.savedEl.childNodes.length;
    }

    selectHound(id) {
      const finder = this.finder();
      const hound = loadHounds().find(item => item.id === id);
      if (!finder || !hound) return;
      const rec = finder.recommendFor(hound);
      if (rec && finder.selectFor(rec)) return;
      // Sold out or not made in this style: open the finder so it can explain.
      finder.load(hound);
      openDialog(this.dataset.target, this);
    }
  }

  // --- Dialog ------------------------------------------------------------------------------------

  function openDialog(id, opener) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog.htsfOpener = opener || null;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('htsf-locked');
    const finder = dialog.querySelector('ht-size-finder');
    if (finder && typeof finder.onOpen === 'function') finder.onOpen();
  }

  let pressedOnBackdrop = false;
  document.addEventListener('pointerdown', event => {
    pressedOnBackdrop = event.target.matches && event.target.matches('dialog.htsf-dialog');
  });

  document.addEventListener('click', event => {
    const target = event.target;
    if (target.matches && target.matches('dialog.htsf-dialog')) {
      if (pressedOnBackdrop) target.close();
      return;
    }
    const opener = target.closest('[data-htsf-open]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.dataset.htsfOpen, opener);
      return;
    }
    // Any link to #size-finder (in a description, say) opens the page's finder.
    const link = target.closest('a[href="#size-finder"]');
    const dialog = link && document.querySelector('dialog.htsf-dialog');
    if (dialog) {
      event.preventDefault();
      openDialog(dialog.id, link);
      return;
    }
    const closer = target.closest('[data-htsf-close]');
    if (closer && closer.closest('dialog')) closer.closest('dialog').close();
  });

  document.addEventListener(
    'close',
    event => {
      const dialog = event.target;
      if (!dialog.matches || !dialog.matches('dialog.htsf-dialog')) return;
      document.documentElement.classList.remove('htsf-locked');
      const opener = dialog.htsfOpener;
      dialog.htsfOpener = null;
      if (opener && document.contains(opener) && !opener.closest('[hidden]')) {
        opener.focus({ preventScroll: true });
      }
    },
    true
  );

  if (!customElements.get('ht-size-finder')) customElements.define('ht-size-finder', HtSizeFinder);
  if (!customElements.get('ht-size-finder-trigger')) {
    customElements.define('ht-size-finder-trigger', HtSizeFinderTrigger);
  }
})();
